import React, { FC, useEffect, useState } from "react";
import {
    View,
    ScrollView,
    Pressable,
    KeyboardAvoidingView,
    Platform,
    useWindowDimensions,
} from "react-native";
import { ThemeConsumer } from "@rneui/themed";
import { shallowEqual } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useIsFocused } from "@react-navigation/native";

// Themes and styles
import { appColors, appFonts, fontSize } from "../../themes/appTheme";
import { OrderSettingsStyle as styles } from "../../themes/components/settings/orderSettingsStyle";

// Common Components
import CommonHeader from "../../shared/commonComponents/commonHeader";
import CommonDialogBox from "../../shared/commonComponents/commonDialogBox";
import Popover from "react-native-popover-view/dist/Popover";

// Core Elements
import {
    ZenButton,
    ZenRadioButton,
    ZenSwitch,
    ZenText,
    ZenToolTip,
    ZenIcon,
    ZenInput,
    ZenLoader,
    ZenToast,
} from "zen-core-elements";

// Redux & APIs
import { useAppDispatch, useAppSelector, getScreenPermission } from "../../redux/hooks";
import { config } from "../../shared/environment";

// Hooks & Types
import useBackHandler from "../../shared/hooks/useBackHandler";
import { useAppNavigation } from "../../types/@react-navigation/navigation";
import {
    RETURN_WINDOW_OPTIONS,
    ReturnWindowOptionValue,
    OrderSettingsFormValues,
} from "./orderSettings.module";
import { getOrderSettingsFromAsyncStorage, getRoundOffFromAsyncStorage, getUpiIdFromAsyncStorage, ROUND_OFF_STORAGE_KEY, UPI_ID_REGEX, UPI_ID_STORAGE_KEY } from "../../utils/asyncstorageHelper";
import { setRoundOffSale } from "../../redux/slices/newSellscreenSlice";

// Unified Yup Validation Schema
const orderSettingsValidationSchema = yup.object().shape({
    returnWindow: yup
        .string()
        .required("Return window is required*")
        .test("is-valid-window", "Enter a valid number between 1 and 365", (val) => {
            if (!val) return false;
            if (["14_DAYS", "30_DAYS", "90_DAYS", "UNLIMITED"].includes(val)) return true;
            if (val === "CUSTOM_DAYS") return false;
            if (val.endsWith("_DAYS")) {
                const parsed = parseInt(val.replace("_DAYS", ""), 10);
                return !isNaN(parsed) && parsed >= 1 && parsed <= 365;
            }
            return false;
        }),
    orderIdPrefixText: yup
        .string()
        .max(10, "Prefix cannot exceed 10 characters")
        .nullable(),
    orderIdSuffixText: yup
        .string()
        .max(10, "Suffix cannot exceed 10 characters")
        .nullable(),
    upiId: yup
        .string()
        .trim()
        // .matches(UPI_ID_REGEX, "Enter a valid UPI ID (e.g. mystore@okaxis)")
        .nullable(),
});

const OrderSettings: FC = () => {
    useBackHandler();
    const navigation = useAppNavigation();
    const dispatch = useAppDispatch();
    const isFocused = useIsFocused();
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;

    // Storage Keys
    const RETURN_WINDOW_STORAGE_KEY = `@order_settings_return_window_${config.storeId}`;
    const ORDER_ID_STORAGE_KEY = `@order_settings_order_id_${config.storeId}`;

    // Permissions
    const { isWrite: isSettingWrite } = useAppSelector(
        getScreenPermission("SettingStack"),
        shallowEqual
    );

    // Screen Loading & Submitting State
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    // Unsaved changes dialog
    const [showLeaveDialog, setShowLeaveDialog] = useState<boolean>(false);

    // Custom days text input state
    const [customDaysText, setCustomDaysText] = useState<string>("");

    // Initial values state for Formik (populated from AsyncStorage on load)
    const [initialFormValues, setInitialFormValues] = useState<OrderSettingsFormValues>({
        returnWindow: "",
        orderIdPrefixText: "",
        orderIdSuffixText: "",
        upiId: "",
        isRoundOff: false,
    });

    // ----------------------------------------------------
    // Unified Formik Form with Section Tracking
    // ----------------------------------------------------
    const formik = useFormik<OrderSettingsFormValues>({
        initialValues: initialFormValues,
        validationSchema: orderSettingsValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setIsSubmitting(true);
            try {
                const updateTasks: Promise<any>[] = [];
                const updatedSections: string[] = [];

                // 1. Return Window tracking -> AsyncStorage
                const isReturnWindowChanged =
                    values.returnWindow !== initialFormValues.returnWindow;

                if (isReturnWindowChanged) {
                    updateTasks.push(
                        AsyncStorage.setItem(
                            RETURN_WINDOW_STORAGE_KEY,
                            values.returnWindow
                        )
                    );
                    updatedSections.push("Return Window");
                }

                // 2. Order ID tracking -> AsyncStorage
                const isOrderIdChanged =
                    values.orderIdPrefixText !== initialFormValues.orderIdPrefixText ||
                    values.orderIdSuffixText !== initialFormValues.orderIdSuffixText;

                if (isOrderIdChanged) {
                    const orderIdData = {
                        orderIdPrefixText: values.orderIdPrefixText,
                        orderIdSuffixText: values.orderIdSuffixText,
                    };
                    updateTasks.push(
                        AsyncStorage.setItem(
                            ORDER_ID_STORAGE_KEY,
                            JSON.stringify(orderIdData)
                        )
                    );
                    updatedSections.push("Order ID");
                }

                // 3. UPI ID tracking -> AsyncStorage
                const upiId = values.upiId?.trim() ?? "";
                if (upiId !== initialFormValues.upiId) {
                    updateTasks.push(
                        upiId
                            ? AsyncStorage.setItem(UPI_ID_STORAGE_KEY, upiId)
                            : AsyncStorage.removeItem(UPI_ID_STORAGE_KEY)
                    );
                    updatedSections.push("UPI Payment");
                }

                // 4. Round Off tracking -> AsyncStorage
                const isRoundOffChanged = values.isRoundOff !== initialFormValues.isRoundOff;
                if (isRoundOffChanged) {
                    updateTasks.push(AsyncStorage.setItem(ROUND_OFF_STORAGE_KEY, String(values.isRoundOff)));
                    updatedSections.push("Round Off");
                }

                // Execute all updated section actions
                if (updateTasks.length > 0) {
                    await Promise.all(updateTasks);
                    // Reprice any open cart with the new setting
                    if (isRoundOffChanged) dispatch(setRoundOffSale(values.isRoundOff));
                    ZenToast({
                        message: `${updatedSections.join(", ")} updated successfully`,
                        type: "success",
                    });
                    if (isReasonsChanged) {
                        // Reload so newly inserted reasons carry their DB ids into the next save
                        await loadAllSettings();
                    } else {
                        const savedValues = { ...values, upiId };
                        setInitialFormValues(savedValues);
                        formik.resetForm({ values: savedValues });
                    }
                } else {
                    ZenToast({ message: "No changes to save", type: "warning" });
                }
            } catch (error: any) {
                ZenToast({
                    message: error?.message || "Failed to update order settings",
                    type: "error",
                });
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    // ----------------------------------------------------
    // Data Fetching (API + AsyncStorage)
    // ----------------------------------------------------
    useEffect(() => {
        if (isFocused) {
            loadAllSettings();
        }
    }, [isFocused]);

    const loadAllSettings = async () => {
        setIsLoading(true);
        try {
            // Retrieve Return Window, Order ID, UPI ID & Round Off from AsyncStorage
            const storageSettings = await getOrderSettingsFromAsyncStorage();
            const storedUpiId = await getUpiIdFromAsyncStorage();
            const storedRoundOff = await getRoundOffFromAsyncStorage();

            if (!["14_DAYS", "30_DAYS", "90_DAYS", "UNLIMITED"].includes(storageSettings.returnWindow)) {
                setCustomDaysText(storageSettings.returnWindow.replace("_DAYS", ""));
            } else {
                setCustomDaysText("");
            }

            const loadedValues: OrderSettingsFormValues = {
                returnWindow: storageSettings.returnWindow,
                orderIdPrefixText: storageSettings.orderIdPrefixText,
                orderIdSuffixText: storageSettings.orderIdSuffixText,
                upiId: storedUpiId,
                isRoundOff: storedRoundOff,
            };

            setInitialFormValues(loadedValues);
            formik.resetForm({ values: loadedValues });
        } catch (error) {
            console.log("Error loading order settings:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // ----------------------------------------------------
    // Navigation / Back Handling
    // ----------------------------------------------------
    const handleBackPress = () => {
        if (formik.dirty) {
            setShowLeaveDialog(true);
        } else {
            navigation.goBack();
        }
    };

    // ----------------------------------------------------
    // Update Button Handler
    // ----------------------------------------------------
    const handleUpdatePress = () => {
        if (formik.dirty) {
            formik.handleSubmit();
        } else {
            ZenToast({ message: "No changes to save", type: "warning" });
        }
    };

    // ----------------------------------------------------
    // Round Off Handler
    // ----------------------------------------------------
    const handleToggleRoundOff = (val: boolean) => {
        formik.setFieldValue("isRoundOff", val);
    };

    // Dialog Props
    const leaveProps = {
        content: "You have unsaved changes. Are you sure you want to leave this page?",
        button: [
            { id: 1, name: "No", color: appColors.primary, backgroundColor: appColors.light },
            { id: 2, name: "Yes", color: appColors.light, backgroundColor: appColors.primary },
        ],
        icon: { type: "material-community", name: "run", color: appColors.primary },
    };

    const handleLeaveConfirmation = (button: { id: number; name: string }) => {
        setShowLeaveDialog(false);
        if (button?.name === "Yes") {
            navigation.goBack();
        }
    };

    // Dynamic Preview Order ID format
    const previewOrderId = `${formik.values.orderIdPrefixText || "ZEN"}123${formik.values.orderIdSuffixText || "ID"}`;

    // Return Window Selection Helpers
    const isCustomSelected =
        !["14_DAYS", "30_DAYS", "90_DAYS", "UNLIMITED"].includes(formik.values.returnWindow) ||
        formik.values.returnWindow === "CUSTOM_DAYS";
    const selectedRadioValue = isCustomSelected ? "CUSTOM_DAYS" : formik.values.returnWindow;

    return (
        <ThemeConsumer>
            {() => (
                <KeyboardAvoidingView
                    behavior={Platform.OS === "ios" ? "padding" : undefined}
                    style={styles.container}
                >
                    <CommonHeader
                        headerName="Order Settings"
                        backIcon
                        backIconOnpress={handleBackPress}
                        rightComponent={
                            isSettingWrite && !isLoading ? (
                                <ZenButton
                                    title={isSubmitting ? "Updating..." : "Update"}
                                    size="sm"
                                    titleColor={appColors.light}
                                    buttonStyle={styles.updateButton}
                                    titleStyle={styles.buttonTitleStyle}
                                    disabled={isSubmitting}
                                    onPress={handleUpdatePress}
                                />
                            ) : undefined
                        }
                    />

                    {/* Top Action Bar with Update Button */}
                    {/* <View style={styles.topActionBar}>
                        {isSettingWrite && (
                            <ZenButton
                                title={isSubmitting ? "Updating..." : "Update"}
                                size="sm"
                                titleColor={appColors.light}
                                buttonStyle={styles.updateButton}
                                titleStyle={styles.buttonTitleStyle}
                                disabled={isSubmitting}
                                onPress={handleUpdatePress}
                            />
                        )}
                    </View> */}

                    {isLoading ? (
                        <ZenLoader
                            lottieType="Local"
                            viewStyle={{ flex: 1, alignItems: "center", justifyContent: "center" }}
                            lottie={require("../../assets/infinityLoader.json")}
                        />
                    ) : (
                        <ScrollView
                            showsVerticalScrollIndicator={false}
                            contentContainerStyle={[
                                styles.scrollContent,
                                { paddingHorizontal: isLandscape ? 20 : 0 },
                            ]}
                        >
                            {/* ==================================================== */}
                            {/* CARD 1: ROUND OFF                                    */}
                            {/* ==================================================== */}
                            <View style={styles.card}>
                                <View style={styles.cardHeaderRow}>
                                    <View style={styles.headerTitleView}>
                                        <ZenText
                                            content="Round Off"
                                            style={styles.cardTitle}
                                        />
                                        <ZenText
                                            content="Saved on this device only."
                                            style={styles.cardSubtitle}
                                        />
                                    </View>
                                </View>

                                <View style={styles.toggleRow}>
                                    <View style={styles.toggleLabelGroup}>
                                        <ZenText
                                            content="Round off grand total"
                                            size={fontSize.mediumFontSize}
                                            bold
                                            color={appColors.lightDark}
                                        />
                                        <ZenToolTip
                                            height={70}
                                            width={240}
                                            backgroundColor={appColors.lightBackgroundColor}
                                            popoverStyle={{ elevation: 5 }}
                                            content={
                                                <ZenText
                                                    content="Rounds the sell screen grand total to the nearest ₹1 (e.g. ₹104.40 → ₹104, ₹48.75 → ₹49). The difference is shown as Round Off."
                                                    size={fontSize.smallerFontSize}
                                                    color={appColors.dark}
                                                />
                                            }
                                            popover={
                                                <Pressable>
                                                    <ZenIcon
                                                        name="information"
                                                        type="material-community"
                                                        size={18}
                                                        color={appColors.primary}
                                                    />
                                                </Pressable>
                                            }
                                        />
                                    </View>
                                    <ZenSwitch
                                        value={formik.values.isRoundOff}
                                        onValueChange={handleToggleRoundOff}
                                        disabled={!isSettingWrite}
                                        activeColor={appColors.primary}
                                        activeThumbColor={appColors.light}
                                        inActiveColor={appColors.borderColor}
                                        inactiveThumbColor={appColors.light}
                                    />
                                </View>
                            </View>

                            {/* ==================================================== */}
                            {/* CARD 2: RETURN WINDOW                                */}
                            {/* ==================================================== */}
                            <View style={styles.card}>
                                <View style={styles.sectionTitleRow}>
                                    <ZenText
                                        content="Return Window"
                                        style={styles.sectionTitle}
                                    />
                                </View>

                                <ZenText
                                    content="Choose a preset return window or enter a custom number of days. You can select one of the following options:"
                                    size={fontSize.smallFontSize}
                                    color={appColors.grey}
                                    style={{ marginBottom: 12 }}
                                />

                                <ZenRadioButton
                                    key={`return-window-${selectedRadioValue}`}
                                    disabled={!isSettingWrite}
                                    initialSelected={selectedRadioValue}
                                    options={RETURN_WINDOW_OPTIONS.map((opt) => ({
                                        id: opt.id,
                                        label: opt.label,
                                        value: opt.value,
                                    }))}
                                    titleStyle={{
                                        color: appColors.lightDark,
                                        fontSize: fontSize.mediumFontSize,
                                        fontFamily: appFonts.medium,
                                    }}
                                    containerStyle={{
                                        flexDirection: isLandscape ? "row" : "column",
                                        flexWrap: "wrap",
                                        gap: 16,
                                        alignItems: "flex-start",
                                    }}
                                    onSelect={(val) => {
                                        if (val === "CUSTOM_DAYS") {
                                            formik.setFieldValue(
                                                "returnWindow",
                                                customDaysText ? `${customDaysText}_DAYS` : "CUSTOM_DAYS"
                                            );
                                        } else {
                                            formik.setFieldValue("returnWindow", val);
                                        }
                                    }}
                                />

                                {/* Custom Days Input (Visible ONLY when Custom Days is selected) */}
                                {isCustomSelected && (
                                    <View style={styles.customDaysContainer}>
                                        <ZenInput
                                            label="Enter Custom Days"
                                            value={customDaysText}
                                            onChangeText={(txt) => {
                                                const numericOnly = txt.replace(/[^0-9]/g, "");
                                                setCustomDaysText(numericOnly);
                                                formik.setFieldValue(
                                                    "returnWindow",
                                                    numericOnly ? `${numericOnly}_DAYS` : "CUSTOM_DAYS"
                                                );
                                            }}
                                            placeholder="e.g. 84"
                                            keyboardType="number-pad"
                                            type="outlined"
                                            containerStyle={{ paddingHorizontal: 0 }}
                                            inputContainerStyle={{ height: 42 }}
                                            errorMessage={
                                                formik.errors.returnWindow && isCustomSelected
                                                    ? formik.errors.returnWindow
                                                    : undefined
                                            }
                                        />
                                    </View>
                                )}

                                {/* Policy Helper Text */}
                                <ZenText
                                    content="The return window begins on the item's delivery date. If the delivery date is unavailable, it starts from the fulfillment date with extra days added for shipping."
                                    style={styles.helperText}
                                />
                            </View>

                            {/* ==================================================== */}
                            {/* CARD 3: ORDER ID                                     */}
                            {/* ==================================================== */}
                            <View style={styles.card}>
                                <View style={styles.sectionTitleRow}>
                                    <ZenText
                                        content="Order ID"
                                        style={styles.sectionTitle}
                                    />

                                </View>

                                {/* Description and Example Preview Box Row */}
                                <View style={styles.orderIdTopRow}>
                                    <ZenText
                                        content="Customize your order ID format to fit your business needs. You can include your store name,current year, or other information for better order navigation."
                                        style={styles.orderIdDescText}
                                    />
                                    <View style={styles.orderIdExampleBox}>
                                        <ZenText
                                            content="Order ID example"
                                            style={styles.orderIdExampleLabel}
                                        />
                                        <ZenText
                                            content={previewOrderId}
                                            style={styles.orderIdExampleValue}
                                        />
                                    </View>
                                </View>

                                {/* Inputs Row: Prefix & Suffix */}
                                <View style={styles.orderIdInputsRow}>
                                    <View style={styles.orderIdInputCol}>
                                        <ZenInput
                                            label="Enter Prefix"
                                            disabled={!isSettingWrite}
                                            value={formik.values.orderIdPrefixText}
                                            onChangeText={(txt) => formik.setFieldValue("orderIdPrefixText", txt)}
                                            placeholder="e.g. REF"
                                            type="outlined"
                                            containerStyle={{ paddingHorizontal: 0 }}
                                            inputContainerStyle={{ height: 42 }}
                                            errorMessage={formik.errors.orderIdPrefixText}
                                        />
                                    </View>
                                    <View style={styles.orderIdInputCol}>
                                        <ZenInput
                                            label="Enter Suffix"
                                            disabled={!isSettingWrite}
                                            value={formik.values.orderIdSuffixText}
                                            onChangeText={(txt) => formik.setFieldValue("orderIdSuffixText", txt)}
                                            placeholder="e.g. TYY"
                                            type="outlined"
                                            containerStyle={{ paddingHorizontal: 0 }}
                                            inputContainerStyle={{ height: 42 }}
                                            errorMessage={formik.errors.orderIdSuffixText}
                                        />
                                    </View>
                                </View>
                            </View>

                            {/* ==================================================== */}
                            {/* CARD 4: UPI PAYMENT                                  */}
                            {/* ==================================================== */}
                            <View style={styles.card}>
                                <View style={styles.sectionTitleRow}>
                                    <ZenText
                                        content="UPI Payment"
                                        style={styles.sectionTitle}
                                    />
                                </View>

                                <ZenText
                                    content="Enter your store's UPI ID. When Online Payment is selected at checkout, a QR code with the payable amount is shown for the customer to scan and pay."
                                    size={fontSize.smallFontSize}
                                    color={appColors.grey}
                                    style={{ marginBottom: 12 }}
                                />

                                <ZenInput
                                    label="Enter UPI ID"
                                    disabled={!isSettingWrite}
                                    value={formik.values.upiId}
                                    onChangeText={(txt) => formik.setFieldValue("upiId", txt.replace(/\s/g, ""))}
                                    placeholder="e.g. mystore@okaxis"
                                    autoCapitalize="none"
                                    autoCorrect={false}
                                    keyboardType="email-address"
                                    type="outlined"
                                    containerStyle={{ paddingHorizontal: 0 }}
                                    inputContainerStyle={{ height: 42 }}
                                    errorMessage={formik.errors.upiId}
                                />
                            </View>
                        </ScrollView>
                    )}

                    {/* Unsaved Changes Popover Dialog */}
                    <Popover
                        isVisible={showLeaveDialog}
                        onRequestClose={() => setShowLeaveDialog(false)}
                        popoverStyle={{
                            width: width > 900 ? width / 2 : width > 600 ? 450 : 350,
                            borderRadius: 10,
                        }}
                    >
                        <CommonDialogBox
                            type="OTHER"
                            dialog={leaveProps}
                            confirmation={handleLeaveConfirmation}
                        />
                    </Popover>
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>
    );
};

export default OrderSettings;